import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';
import { NavLink, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faUser, faHome, faSun, faMoon, faPalette } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebookF } from '@fortawesome/free-brands-svg-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';

import styles from './Navbar.module.css';

function Navbar() {
  const { theme, toggleTheme, primaryColor, setPrimaryColor, themes } = useTheme();
  const { openAuthModal } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(pb.authStore.isValid ? pb.authStore.model : null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenus = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    // Subscribe to auth changes
    const removeListener = pb.authStore.onChange(() => {
      setUser(pb.authStore.isValid ? pb.authStore.model : null);
    }, true);

    return () => {
      removeListener();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navLinks = [
    { id: "accueil", to: "/", text: "Accueil" },
    { id: "services", to: "/services", text: "Prestations" },
    { id: "realisations", to: "/realisations", text: "Réalisations" },
    { id: "qui-sommes-nous", to: "/qui-sommes-nous", text: "Qui sommes-nous ?" },
    { id: "notre-cooperative", to: "/notre-cooperative", text: "Notre Coopérative" },
    { id: "recrutement", to: "/recrutement", text: "Recrutement" },
    { id: "contact", to: "/contact", text: "Contact" },
  ];

  return (
    <nav className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <button className={styles.burgerIcon} onClick={toggleMenu} aria-label="Ouvrir le menu de navigation">
          <FontAwesomeIcon icon={faBars} />
        </button>
        <Link to="/" onClick={closeMenus} className={styles.logoLink}>
          <img src="/images/logo.png" alt="Maison Quinard Logo" className={styles.navbarTitleImage} />
        </Link>
        <ul className={styles.navLinksDesktop}>
          {navLinks.map((link) => (
            <li key={link.id}>
              <NavLink
                to={link.to}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ''}`
                }
                onClick={closeMenus}
              >
                {link.text}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className={styles.rightIcons}>
          {/* Theme Color Dropdown for Public Navbar */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowThemeDropdown(!showThemeDropdown)}
              className={styles.themeToggle}
              title="Changer le thème"
              style={{
                color: showThemeDropdown ? (themes[primaryColor] || themes['default']).primary : 'inherit'
              }}
            >
              <FontAwesomeIcon icon={faPalette} />
            </button>
            {showThemeDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: theme === 'dark' ? '#1e293b' : 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                zIndex: 100,
                minWidth: '280px',
                padding: '1rem'
              }}>
                <div style={{
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid var(--color-light-gray)'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '0.95rem',
                    fontWeight: '700',
                    color: theme === 'dark' ? '#f1f5f9' : '#334155'
                  }}>
                    <FontAwesomeIcon icon={faPalette} style={{ marginRight: '8px' }} />
                    Choisir un thème
                  </h4>
                </div>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {Object.entries(themes).map(([key, themeOption]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setPrimaryColor(key);
                        setShowThemeDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: `2px solid ${primaryColor === key ? themeOption.primary : '#e2e8f0'}`,
                        borderRadius: '6px',
                        background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : (primaryColor === key ? '#f8fafc' : 'white'),
                        cursor: 'pointer',
                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                        textAlign: 'left',
                        fontSize: '0.9rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (primaryColor !== key) {
                          e.currentTarget.style.borderColor = themeOption.primary;
                          e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#f1f5f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (primaryColor !== key) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'white';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.5rem' }}>{themeOption.icon}</span>
                        <span style={{
                          fontWeight: primaryColor === key ? '600' : '500'
                        }}>
                          {themeOption.name}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: themeOption.primary,
                          border: '2px solid white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}></span>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: themeOption.accent,
                          border: '2px solid white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}></span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={toggleTheme}
            className={styles.themeToggle}
            aria-label={theme === 'light' ? 'Activer le mode sombre' : 'Activer le mode clair'}
          >
            <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
          </button>

          <a href="https://www.instagram.com/maison_bois_quinard/" target="_blank" rel="noopener noreferrer" className={styles.socialIcon} aria-label="Page Instagram de Maison Quinard">

            <FontAwesomeIcon icon={faInstagram} />
          </a>
          <a href="https://www.facebook.com/MaisonQuinard" target="_blank" rel="noopener noreferrer" className={styles.socialIcon} aria-label="Page Facebook de Maison Quinard">
            <FontAwesomeIcon icon={faFacebookF} />
          </a>
          <div className={styles.separator}></div>

          {user ? (
            <>
              {user.role === 'admin' && (
                <Link to="/admin" className={styles.socialIcon} aria-label="Dashboard Administrateur">
                  <FontAwesomeIcon icon={faHome} />
                </Link>
              )}
              <Link to="/account" className={styles.socialIcon} aria-label="Mon Compte">
                <FontAwesomeIcon icon={faUser} />
              </Link>
            </>
          ) : (
            <button
              onClick={() => openAuthModal('login')}
              className={styles.socialIcon}
              aria-label="Connexion"
              style={{ background: 'none', border: 'none', padding: 0 }}
            >
              <FontAwesomeIcon icon={faUser} />
            </button>
          )}
        </div>

        <div className={`${styles.navOverlay} ${isOpen ? styles.navOpen : ''}`}>
          <button className={styles.closeNav} onClick={toggleMenu} aria-label="Fermer le menu de navigation">
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className={styles.navOverlayContent}>
            <ul className={styles.navLinksMobile}>
              {navLinks.map((link, index) => (
                <li key={link.id} style={{ '--index': index }}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `${styles.mobileNavLink} ${isActive ? styles.activeMobile : ''}`
                    }
                    onClick={closeMenus}
                  >
                    {link.text}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className={styles.navOverlayBottom}>
              <div className={styles.socialLinksMobile}>
                <a href="https://www.instagram.com/maison_bois_quinard/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a href="https://www.facebook.com/MaisonQuinard" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FontAwesomeIcon icon={faFacebookF} />
                </a>
              </div>

              <div className={styles.mobileAuthButtons} style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', alignItems: 'center' }}>
                {!user ? (
                  <button
                    onClick={() => {
                      closeMenus();
                      openAuthModal('login');
                    }}
                    className={styles.themeToggleFull}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <FontAwesomeIcon icon={faUser} />
                    <span>Connexion</span>
                  </button>
                ) : (
                  <Link to="/account" onClick={closeMenus} className={styles.themeToggleFull} style={{ width: '100%', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={faUser} />
                    <span>Mon Compte</span>
                  </Link>
                )}
              </div>

              <button
                onClick={toggleTheme}
                className={styles.themeToggleFull}
              >
                <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
                <span>{theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}</span>
              </button>
            </div>
          </div>
        </div>

        {isOpen && (
          <div className={styles.backdrop} onClick={closeMenus}></div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
